import React, { useState } from 'react';
import { Sparkles, ArrowRight, Activity, Brain, Globe, CheckCircle2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEligibilityLimit } from '../../hooks/useEligibilityLimit';
import AIProcessingLab from '../shared/AIProcessingLab';
import AdvisorContactForm from '../shared/AdvisorContactForm';

type Step = 'input' | 'diagnostic' | 'result';

interface EnglishPrediction {
    predictedScore: string;
    proficiencyLevel: string;
    gaps: string[];
    universityMatchOdds: string;
    verdict: string; // New dynamic field
}

const EnglishPrepLab = () => {
    const navigate = useNavigate();
    const { canAccess, loading, markAsUsed } = useEligibilityLimit('english-prep');
    const [step, setStep] = useState<Step>('input');
    const [diagnosticText, setDiagnosticText] = useState('');
    const [showAdvisorForm, setShowAdvisorForm] = useState(false);
    const [result, setResult] = useState<EnglishPrediction | null>(null);

    // Form State
    const [targetExam, setTargetExam] = useState('IELTS (UKVI)');
    const [mockType, setMockType] = useState('Not Yet / First Time');
    const [scoreInput, setScoreInput] = useState('');

    const runDiagnostic = async (e: React.FormEvent) => {
        e.preventDefault();
        setStep('diagnostic');

        // Sequential Animation Sequence
        const sequence = [
            { text: "[SCAN] Parsing Linguistic DNA & Syntax...", delay: 0 },
            { text: "[AUDIT] Mapping CEFR Proficiency Levels...", delay: 800 },
            { text: "[SYNC] Benchmarking against Global Entry Nodes...", delay: 1600 },
            { text: "[PREDICT] Finalizing Band Score Probability...", delay: 2400 },
        ];

        // Trigger animations
        sequence.forEach(({ text, delay }) => {
            setTimeout(() => setDiagnosticText(text), delay);
        });

        try {
            const response = await fetch('/api/predict-english', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetExam, mock: mockType, score: scoreInput || 'N/A' })
            });

            const data = await response.json();

            // Wait for animation to finish (approx 3.2s total) before showing result
            setTimeout(() => {
                setResult(data);
                setStep('result');
                markAsUsed();
            }, 3500);

        } catch (error) {
            console.error("Diagnostic failed", error);
            // Fallback for demo
            setTimeout(() => {
                setResult({
                    predictedScore: "6.5",
                    proficiencyLevel: "B2 - Upper Intermediate",
                    gaps: ["Advanced Grammar Structures", "Academic Vocabulary Range", "Coherence & Cohesion"],
                    universityMatchOdds: "Moderate for Russell Group",
                    verdict: "Your profile shows good potential, but there are a few areas that may require improvement before you move forward. Strengthening these now will increase your chances of a smoother application and arrival process."
                });
                setStep('result');
                markAsUsed();
            }, 3500);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Access Rights...</div>;

    if (!canAccess) {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8 space-y-6">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <Lock size={32} className="text-gray-400" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-[#011E26]">Free Diagnostic Limit Reached</h3>
                    <p className="text-slate-500 mt-2 max-w-md mx-auto">
                        You have already used your free English diagnostic. Please sign in or create an account to access unlimited AI predictions and detailed reports.
                    </p>
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-3 bg-white border border-gray-200 text-[#011E26] font-bold rounded hover:border-[#009B8F] transition-all"
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() => navigate('/signup')}
                        className="px-6 py-3 bg-[#009B8F] text-white font-bold rounded hover:bg-[#00857a] transition-all"
                    >
                        Create Free Account
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'diagnostic') {
        return <AIProcessingLab text={diagnosticText} />;
    }

    if (step === 'result' && result) {
        return (
            <div className="animate-fadeIn max-w-2xl mx-auto">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                        <CheckCircle2 size={14} /> Intelligence Scan Complete
                    </div>
                    <h2 className="text-3xl font-serif text-[#011E26]">Diagnostic Report</h2>
                </div>

                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    {/* Header - Score */}
                    <div className="bg-[#011E26] p-8 text-white text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-[#009B8F] rounded-full filter blur-[60px] opacity-20"></div>
                        <p className="text-[#009B8F] font-bold text-xs uppercase tracking-widest mb-2">Predicted Baseline</p>
                        <h1 className="text-6xl font-bold mb-2">{result.predictedScore}</h1>
                        <p className="text-slate-400 text-lg">{result.proficiencyLevel}</p>
                    </div>

                    {/* Recommendation Message */}
                    <div className="bg-orange-50 border-b border-orange-100 p-6 text-center">
                        <p className="text-[#011E26] text-sm/relaxed font-medium italic">
                            "{result.verdict || "Your profile shows potential, but specific improvements are needed for top-tier admissions."}"
                        </p>
                    </div>

                    {/* Insights (Blurred) */}
                    <div className="p-8">
                        <div className="space-y-6 filter blur-sm select-none opacity-50">
                            <div>
                                <h4 className="font-bold text-[#011E26] mb-3 flex items-center gap-2"><Brain size={18} /> Linguistic Gaps Identified</h4>
                                <ul className="space-y-2">
                                    {result.gaps.map((gap, i) => (
                                        <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div> {gap}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-bold text-[#011E26] mb-3 flex items-center gap-2"><Globe size={18} /> University Match Odds</h4>
                                <p className="text-slate-600 text-sm p-3 bg-gray-50 rounded">{result.universityMatchOdds}</p>
                            </div>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="bg-[#011E26] p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#009B8F] rounded-full filter blur-[60px] opacity-20"></div>
                        {!showAdvisorForm ? (
                            <div className="grid grid-cols-1 gap-3 relative z-10">
                                <button
                                    onClick={() => setShowAdvisorForm(true)}
                                    className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#009B8F]/20"
                                >
                                    Request Expert Review <ArrowRight size={16} />
                                </button>
                                <button
                                    onClick={() => navigate('/signup')}
                                    className="w-full py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm uppercase tracking-widest rounded border border-white/20 transition-all flex items-center justify-center gap-2"
                                >
                                    Sign Up to Unlock Full Report
                                </button>
                            </div>
                        ) : (
                            <div className="relative z-10">
                                <AdvisorContactForm
                                    serviceType="english-prep"
                                    onCancel={() => setShowAdvisorForm(false)}
                                    initialData={{
                                        predicted_score: result.predictedScore,
                                        proficiency: result.proficiencyLevel
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={runDiagnostic} className="space-y-8 animate-fadeIn">
            <div className="mb-8">
                <h3 className="text-2xl font-serif text-[#011E26] mb-2">English Prep Hub</h3>
                <p className="text-slate-600">Global Exam Diagnostic & Prediction Engine</p>
            </div>

            {/* 1. Target Exam */}
            <div>
                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Target High-Stakes Exam</label>
                <select
                    value={targetExam}
                    onChange={(e) => setTargetExam(e.target.value)}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                >
                    <optgroup label="United Kingdom (UKVI)">
                        <option>IELTS for UKVI</option>
                        <option>PTE Academic UKVI</option>
                        <option>LanguageCert SELT</option>
                    </optgroup>
                    <optgroup label="USA">
                        <option>TOEFL iBT</option>
                        <option>Duolingo English Test (DET)</option>
                        <option>IELTS Academic</option>
                    </optgroup>
                    <optgroup label="Canada">
                        <option>IELTS Academic</option>
                        <option>PTE Academic</option>
                        <option>CAEL</option>
                    </optgroup>
                    <optgroup label="Europe">
                        <option>IELTS</option>
                        <option>TOEFL</option>
                        <option>Cambridge C1/C2</option>
                    </optgroup>
                    <option value="Not Sure">Not Sure Yet (AI Recommendation)</option>
                </select>
            </div>

            {/* 2. Mock History */}
            <div>
                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Last Mock Test Experience</label>
                <select
                    value={mockType}
                    onChange={(e) => setMockType(e.target.value)}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                >
                    <option value="Not Yet / First Time">Not Yet / First Time</option>
                    <option value="Official Mock">Taken Official Online Mock</option>
                    <option value="Third-Party Mock">Taken Third-Party Mock</option>
                    <option value="Real Exam">Taken Real Exam Previously</option>
                </select>
            </div>

            {/* 3. Input/Score */}
            {mockType !== 'Not Yet / First Time' && (
                <div className="animate-fadeIn">
                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Latest Score / Band</label>
                    <input
                        type="text"
                        required
                        value={scoreInput}
                        onChange={(e) => setScoreInput(e.target.value)}
                        placeholder="e.g. 6.5, 82, or 105"
                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                    />
                </div>
            )}

            {mockType === 'Not Yet / First Time' && (
                <div className="animate-fadeIn p-4 bg-blue-50 border border-blue-100 rounded text-sm text-blue-800 flex items-start gap-3">
                    <Sparkles className="flex-shrink-0 mt-0.5" size={16} />
                    <p>AI will perform a full diagnostic based on your profile and linguistic patterns.</p>
                </div>
            )}

            <button
                type="submit"
                className="w-full py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2 group"
            >
                <Sparkles size={16} className="group-hover:animate-spin" /> Run Intelligence Scan
            </button>
        </form>
    );
};

export default EnglishPrepLab;
